<script lang="ts" setup>
//--------------------------------------------------
// Imports & Composables
//--------------------------------------------------
import { useWebQuoteManager } from '@/services/webQuoteManager';
import { useFormatters } from '@/util/formatters';
import { SalesOrder } from '@/types/salesOrder';
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';
import { getAuthContext } from '@/services/auth';
import BaseModal from '@/components/modals/BaseModal.vue';
import { useApiEnvStore } from '@/services/env';

//--------------------------------------------------
// Service Initialization
//--------------------------------------------------
const webQ = useWebQuoteManager();
const { formatCurrency } = useFormatters();
const envStore = useApiEnvStore();

//--------------------------------------------------
// Reactive State
//--------------------------------------------------
const salesOrderDescription = ref<string>('');
const baid = ref<string>('');
const poNumber = ref<string>('');

const loginUsername = ref<string>('');
const loginPassword = ref<string>('');

//--------------------------------------------------
// Watchers
//--------------------------------------------------
watch(
	() => webQ.selectedWebQuote.value,
	(newValue) => {
		if (!newValue) {
			salesOrderDescription.value = '';
			baid.value = '';
			poNumber.value = '';
			return;
		}
		salesOrderDescription.value = `WebQuote#${newValue.id}`;
	}
);

//--------------------------------------------------
// UI / Process State
//--------------------------------------------------
const isRunning = ref<boolean>(false);
const isLoggingIn = ref<boolean>(false);
const loginOperation = ref<boolean>(false);
const loginFailed = ref<boolean>(false);

function closeSlideOutModal() {
	if (isRunning.value) return;
	webQ.clearSelectedWebQuote();
}

function closeLoginModal() {
	if (isRunning.value) return;
	isLoggingIn.value = false;
	loginUsername.value = '';
	loginPassword.value = '';
	loginFailed.value = false;
	loginOperation.value = false;
}

//--------------------------------------------------
// Sales Order Creation Flow
//--------------------------------------------------
async function createSalesOrder() {
	// ==============================
	// Pre-Process Validation
	// ==============================
	// Ensure web quote is selected
	if (!webQ.selectedWebQuote.value) return;

	// Ensure user is logged in and verified
	let userId: string;
	let idToken: string;
	try {
		({ userId, idToken } = await getAuthContext());
	} catch {
		alert('Request can not be processed: unable to verify user');
		return;
	}

	// Ensure non-empty form values
	if (!baid.value || !poNumber.value) {
		alert("'BAID' and 'PO Number' cannot be empty");
		isRunning.value = false;
		return;
	}

	// ==============================
	// UI Update
	// ==============================
	isRunning.value = true;

	// ==============================
	// Server Call
	// ==============================
	const salesOrder: SalesOrder = {
		baid: baid.value,
		poNumber: poNumber.value,
		description: salesOrderDescription.value,
		items: webQ.selectedWebQuote.value.items
	};

	// Attempt sales order creation
	let createSalesOrderResponseJson: any;
	let soCreated: string | null = null;
	let userVerified: boolean = false;
	try {
		const createSalesOrderResponse = await fetch(`${envStore.apiBaseUrl}/CreateSalesOrder`, {
			method: 'POST',
			headers: {
				Authorization: `Bearer ${idToken}`
			},
			body: JSON.stringify({
				uid: userId,
				salesOrder
			})
		});
		if (!createSalesOrderResponse.ok) {
			throw new Error('Network response was not ok');
		}
		createSalesOrderResponseJson = await createSalesOrderResponse.json();
		soCreated = createSalesOrderResponseJson.salesOrderCreated;
		userVerified = createSalesOrderResponseJson.userVerified;
	} catch (error) {
		alert('Request cannot be processed: network error');
		isRunning.value = false;
		return;
	}

	// ==============================
	// Post-Process UI Updates
	// ==============================
	// Alert user of success if sales order number was found
	if (soCreated) {
		alert('Sales Order Created');
		isRunning.value = false;
		webQ.clearSelectedWebQuote();
		isRunning.value = false;
		return;
	}

	// Alert user if there was an issue with creating the sales order
	else if (userVerified && !soCreated) {
		alert('Request cannot be processed: sales order creation failed');
		isRunning.value = false;
		isRunning.value = false;
		return;
	}

	// Alert user if they are not verified to create sales orders
	else if (!userVerified) {
		alert('Acumatica session invalid. Please re-authenticate.');
		isLoggingIn.value = true;
		isRunning.value = false;
		return;
	}
}
//--------------------------------------------------
// Export to CSV Flow
//--------------------------------------------------
async function exportCsv() {
	// ==============================
	// Pre-Process Validation
	// ==============================
	// Ensure web quote is selected
	if (!webQ.selectedWebQuote.value) return;

	// Ensure user is logged in and verified
	let userId: string;
	let idToken: string;
	try {
		({ userId, idToken } = await getAuthContext());
	} catch {
		alert('Request can not be processed: unable to verify user');
		return;
	}

	// ==============================
	// UI Update
	// ==============================
	isRunning.value = true;

	// ==============================
	// Server Call
	// ==============================
	const items: SalesOrderItem = webQ.selectedWebQuote.value.items;

	// ==============================
	// Preparing CSV
	// ==============================
	const headers = Object.keys(items[0]);
	const rows = items.map((item) => headers.map((header) => escapeCsv(item[header])).join(','));
	const csv = [headers.join(','), ...rows].join('\n');
	const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
	const url = URL.createObjectURL(blob);

	const link = document.createElement('a');
	link.href = url;
	link.download = `${webQ.selectedWebQuote.value.id}.csv`;
	link.click();

	URL.revokeObjectURL(url);

	// ==============================
	// Post-Process UI Updates
	// ==============================
	// Alert user of success if sales order number was found
	alert('Process complete');
	isRunning.value = false;
	webQ.clearSelectedWebQuote();
	isRunning.value = false;
	return;
}

function escapeCsv(value) {
	if (value == null) return '';

	const string = String(value);

	if (string.includes(',') || string.includes('"') || string.includes('\n')) {
		return `"${string.replaceAll('"', '""')}"`;
	}

	return string;
}

//--------------------------------------------------
// Login To Acumatica Flow
//--------------------------------------------------
async function loginToAcumatica() {
	// Clear UI state
	loginFailed.value = false;

	// Ensure user is logged in and verified
	let userId: string;
	let idToken: string;
	try {
		({ userId, idToken } = await getAuthContext());
	} catch {
		alert('Request can not be processed: unable to verify user');
		return;
	}

	// Ensure username and password are provided
	if (!loginUsername.value || !loginPassword.value) {
		alert('Request cannot be processed: missing username or password.');
		isRunning.value = false;
		isRunning.value = false;
		return;
	}

	// Lock UI
	isRunning.value = true;
	loginOperation.value = true;

	// Attempt login to Acumatica
	let loginSuccess: boolean = false;
	try {
		const loginToAcumaticaResponse = await fetch(`${envStore.apiBaseUrl}/LoginToAcumatica`, {
			method: 'POST',
			headers: {
				Authorization: `Bearer ${idToken}`
			},
			body: JSON.stringify({
				uid: userId,
				username: loginUsername.value,
				password: loginPassword.value
			})
		});
		if (!loginToAcumaticaResponse.ok) {
			throw new Error('Network response was not ok');
		}

		const loginToAcumaticaResponseJson = await loginToAcumaticaResponse.json();
		loginSuccess = loginToAcumaticaResponseJson.loginSuccess;
	} catch {
		alert('Request cannot be processed: network error');
		isRunning.value = false;
		loginOperation.value = false;
		return;
	}

	if (loginSuccess) {
		alert('Re-authentication successful. Please try creating the sales order again.');
		isRunning.value = false;
		closeLoginModal();
	} else {
		alert('Re-authentication failed. Please check your credentials and try again.');
		isRunning.value = false;
		loginOperation.value = false;
		loginFailed.value = true;
	}
}

//--------------------------------------------------
// Lifecycle Hooks
//--------------------------------------------------
onMounted(async () => {
	window.addEventListener('beforeunload', handleUnload);
	await webQ.fetchWebQuotes();
});

onBeforeUnmount(async () => {
	window.removeEventListener('beforeunload', handleUnload);
});

//--------------------------------------------------
// Navigation Guards
//--------------------------------------------------
onBeforeRouteLeave((to, from) => {
	if (isRunning.value) {
		return confirm('A process is still running. Are you sure you want to leave?');
	}
});

//--------------------------------------------------
// Window Event Handlers
//--------------------------------------------------
function handleUnload(event: BeforeUnloadEvent) {
	if (isRunning.value) {
		event.preventDefault();
		event.returnValue = '';
	}
}
</script>

<template>
	<div class="web-quotes-view" :style="{ cursor: isRunning ? 'wait' : 'default' }">
		<!-- Title and Search Bar -->
		<div class="web-quotes-view__header">
			<div class="header__titles">
				<h1 class="fira aliceblue">Web Quotes</h1>
				<p class="fira aliceblue semi-large">Qteedy Web Quotes</p>
			</div>
			<input placeholder="Search..." v-model="webQ.searchQuery.value" />
		</div>

		<!-- Main Content -->
		<div class="web-quotes-view__content">
			<!-- Tables -->
			<div class="web-quotes-view__tables">
				<!-- Table Header and Table Body -->
				<div class="tables__table-header">
					<table class="main-table">
						<colgroup>
							<col style="width: 75px" />
							<col style="min-width: 200px; width: auto" />
							<col style="width: 200px" />
							<col style="width: 200px" />
						</colgroup>
						<thead>
							<tr>
								<th v-for="heading in webQ.tableHeadings" :key="heading">
									{{ heading }}
								</th>
							</tr>
						</thead>
					</table>
				</div>
				<div class="tables__main-table">
					<table class="main-table">
						<colgroup>
							<col style="width: 75px" />
							<col style="width: auto" />
							<col style="width: 200px" />
							<col style="width: 200px" />
						</colgroup>
						<tbody>
							<tr
								v-for="row in webQ.rowData.value"
								:key="row[0]"
								:class="{ selected: row[0] === webQ.selectedWebQuoteId.value }"
								@click="webQ.selectWebQuote(row[0])"
							>
								<td v-for="(cell, cellIndex) in row" :key="cellIndex">
									{{ cell }}
								</td>
							</tr>
						</tbody>
					</table>
				</div>

				<!-- Pagination Buttons -->
				<div class="tables__pagination-buttons">
					<button :disabled="webQ.isFirstPage.value" @click="webQ.firstPage" class="icon-button">
						<span class="material-symbols-outlined">first_page</span>
					</button>
					<button :disabled="webQ.isFirstPage.value" @click="webQ.prevPage" class="icon-button">
						<span class="material-symbols-outlined">chevron_left</span>
					</button>
					<p>{{ webQ.currentPage }}</p>
					<button :disabled="webQ.isLastPage.value" @click="webQ.nextPage" class="icon-button">
						<span class="material-symbols-outlined">chevron_right</span>
					</button>
					<button :disabled="webQ.isLastPage.value" @click="webQ.lastPage" class="icon-button">
						<span class="material-symbols-outlined">last_page</span>
					</button>
				</div>
			</div>
		</div>

		<!-- Footer -->
		<div class="web-quotes-view__footer">
			<button :disabled="webQ.filterBy.value === webQ.FilterByValue.all" @click="webQ.setFilterBy(webQ.FilterByValue.all)">View: All</button>
			<button :disabled="webQ.filterBy.value === webQ.FilterByValue.last_1" @click="webQ.setFilterBy(webQ.FilterByValue.last_1)">
				View: Last 24 Hours
			</button>
			<button :disabled="webQ.filterBy.value === webQ.FilterByValue.last_7" @click="webQ.setFilterBy(webQ.FilterByValue.last_7)">
				View: Last 7 Days
			</button>
			<button :disabled="webQ.filterBy.value === webQ.FilterByValue.last_30" @click="webQ.setFilterBy(webQ.FilterByValue.last_30)">
				View: Last 30 Days
			</button>
			<div class="metrics">
				<p class="aliceblue fira">Count: {{ webQ.filteredWebQuotesCount.value }}</p>
				<p class="aliceblue fira">Value: {{ formatCurrency(webQ.filteredWebQuotesValue.value) }}</p>
			</div>
		</div>
	</div>

	<!-- Slide Out Modal -->
	<div
		v-if="webQ.selectedWebQuote.value"
		class="slideOutModal-backdrop"
		@click="closeSlideOutModal"
		:style="{ cursor: isRunning ? 'wait' : 'default' }"
	>
		<div class="slideOutModal-container" @click.stop>
			<div class="webQuote__header">
				<p class="semi-large">
					Web Quote:
					<a :href="webQ.selectedWebQuote.value.link" target="_blank">
						{{ webQ.selectedWebQuote.value.id }}
					</a>
				</p>
			</div>
			<div class="webQuote__info">
				<p class="field-header">Customer:</p>
				<p>{{ webQ.selectedWebQuote.value.customer }}</p>
				<p class="field-header">Total Value:</p>
				<p>{{ formatCurrency(webQ.selectedWebQuote.value.total) }}</p>
				<label for="baid" class="field-header">BAID:</label>
				<input id="baid" class="modal" v-model="baid" :disabled="isRunning" />
				<label for="po" class="field-header">PO Number:</label>
				<input id="po" class="modal" v-model="poNumber" :disabled="isRunning" />
				<label for="desc" class="field-header">Description:</label>
				<input id="desc" class="modal" v-model="salesOrderDescription" :disabled="isRunning" />
				<label for="notes" class="field-header">Qteedy Notes:</label>
				<textarea id="notes" disabled :value="webQ.selectedWebQuote.value.notes ?? ''" :style="{ cursor: isRunning ? 'wait' : 'text' }" />
			</div>
			<p class="field-header fira">Items:</p>
			<div class="webQuote__items-table">
				<table class="items-table">
					<colgroup>
						<col style="width: 62%" />
						<col style="width: 15%" />
						<col style="width: 23%" />
					</colgroup>
					<thead>
						<tr>
							<th>Inventory ID</th>
							<th>Qty</th>
							<th>Disc (%)</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="item in webQ.selectedWebQuote.value.items" :key="item">
							<td>
								{{ item.inventoryId }}<br /><span class="secondary-text">{{ item.variantTitle }}</span>
							</td>
							<td>{{ item.quantity }}</td>
							<td>{{ Math.round(item.discountPercent * 10) / 10 }}</td>
						</tr>
					</tbody>
				</table>
			</div>
			<div class="webQuote__actions">
				<button :disabled="isRunning" class="light" @click="createSalesOrder">Create SO</button>
				<button :disabled="isRunning" class="light" @click="webQ.clearSelectedWebQuote()">Close</button>
				<button :disabled="isRunning" class="light" @click="exportCsv">Export to CSV</button>
			</div>
		</div>
	</div>

	<!-- Login Modal -->
	<BaseModal v-if="isLoggingIn" @close="closeLoginModal">
		<form class="login-modal" @submit.prevent="loginToAcumatica">
			<p>
				<span style="display: block"><b>Login To Acumatica</b></span>
				<span><i>Your user session has expired or does not exist.</i></span>
			</p>
			<div class="login-form-fields">
				<label for="username">Username:</label>
				<input class="modal" id="username" v-model="loginUsername" />
				<label for="password">Password:</label>
				<input class="modal" id="password" type="password" v-model="loginPassword" />
			</div>
			<div class="login-modal__actions">
				<!-- <button class="light" @click="loginToAcumatica" :disabled="isRunning">Login</button> -->
				<button class="light" type="submit" :disabled="isRunning">Login</button>
				<button class="light" @click="closeLoginModal" :disabled="isRunning">Cancel</button>
			</div>
			<div class="login-modal__operationInfo">
				<p v-if="loginOperation">Logging in to Acumatica...</p>
				<p v-if="loginFailed" style="color: red">Login failed. Please try again.</p>
			</div>
		</form>
	</BaseModal>
</template>

<style scoped>
/* Main Layout */
.web-quotes-view {
	height: 100vh;
	display: flex;
	flex-direction: column;
	overflow: hidden;
	user-select: none;
}

.web-quotes-view__header {
	display: flex;
	justify-content: space-between;
	margin: 15px;
}

.web-quotes-view__content {
	flex: 1;
	min-height: 0;
	display: flex;
	gap: 15px;
}

.web-quotes-view__footer {
	display: flex;
	padding: 15px 0 15px 15px;
	gap: 15px;
}

.web-quotes-view__tables {
	flex: 1;
	margin: 0 15px;
	display: flex;
	flex-direction: column;
}

.header__titles {
	h1 {
		font-weight: normal;
		margin: 0;
		font-size: 1.5rem;
	}

	p {
		color: rgb(149, 156, 161);
		font-size: 1.2rem;
	}
}

.tables__table-header {
	background: #4b525a;
	border-radius: 5px 5px 0 0;
	margin-top: 5px;
	overflow-y: auto;
	scrollbar-gutter: stable;
}

.tables__main-table {
	max-height: 100%;
	overflow-y: auto;
	background-color: #4b525a;
	flex: 1;
	scrollbar-gutter: stable;
}

/* Table */
table.main-table {
	width: 100%;
	border-collapse: collapse;
	background: transparent;
	font-family: 'Fira Sans', serif;
	flex: 1;
	table-layout: fixed;
}

table.main-table thead {
	border-bottom: 1px solid #272b2f;
	text-align: left;
	color: aliceblue;
	top: 0;
	position: sticky;
	opacity: 100;
}

table.main-table thead th {
	padding: 15px 15px;
	font-weight: normal;
}

table.main-table tbody {
	max-height: 100%;
	overflow-y: auto;
}

table.main-table tbody td {
	padding: 15px 15px;
	white-space: pre-line;
	font-weight: 200;
	color: aliceblue;
}

table.main-table tbody tr:hover {
	background: #646e79;
	cursor: pointer;
}

table.main-table tbody tr.selected {
	background: #646e79;
}

table.main-table tbody tr.disabled {
	opacity: 0.5;
	pointer-events: none;
}

/* Input */
input {
	width: 250px;
	font-family: 'Fira Sans';
	height: fit-content;
	border: #272b2f 2px solid;
	font-size: 0.75rem;
	padding: 2px 5px;
	border-radius: 5px;
	box-sizing: border-box;
	align-self: flex-end;
}

input:focus {
	border: #4b525a 2px solid;
	outline: none;
}

/* Others */
.status-indicator {
	.material-symbols-outlined {
		font-size: 18px;

		font-variation-settings: 'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 20;
	}

	font-family: 'Fira Sans';
	color: #1b5299;
	align-self: center;
	display: flex;
	align-items: center;
	gap: 5px;
}

.metrics {
	display: flex;
	gap: 15px;
	align-items: center;

	p {
		color: aliceblue;
		font: 'Fira Sans';
		gap: 15px;
	}
}

.tables__pagination-buttons {
	background-color: #4b525a;
	border-radius: 0 0 5px 5px;
	display: flex;

	justify-content: flex-end;
	padding-right: 15px;

	border-top: 1px solid #272b2f;

	p {
		margin: 5px 5px 0 5px;
		color: rgb(189, 195, 201);
	}
}

/* Modal */
.slideOutModal-backdrop {
	position: fixed;
	inset: 0;
	background: rgba(0, 0, 0, 0.5);
	display: flex;
	justify-content: right;
	align-items: center;
	z-index: 1000;
}

.slideOutModal-container {
	background: #f5f5f5;
	width: 450px;
	height: 80vh;
	box-shadow: -2px 0 10px rgba(0, 0, 0, 0.3);
	display: flex;
	gap: 8px;
	flex-direction: column;
	padding: 20px;
	overflow-y: auto;
	border-radius: 8px 0 0 8px;
}

.webQuote__header {
	font-family: 'Fira Sans';
}

.webQuote__info {
	display: grid;
	grid-template-columns: max-content 1fr;
	column-gap: 15px;
	row-gap: 3px;
}

.webQuote__info p,
.webQuote__info label {
	font-family: 'Fira Sans';
	font-size: 0.85rem;
}

.field-header {
	font-weight: 500;
}

textarea {
	resize: none;
	height: 100px;
}

table.items-table {
	font-size: 0.85rem;
	table-layout: fixed;
	width: 100%;
	border: 1px solid #d3d3d3;
	border-radius: 3px;
	border-collapse: collapse;
	border-spacing: 0;
	font-family: 'Fira Sans';

	thead {
		border-bottom: 1px solid #d3d3d3;
		background-color: #e8e8e8;
	}

	thead th {
		padding: 3px 0 3px 5px;
		font-weight: normal;
		text-align: left;
	}

	tbody {
		max-height: 100%;
		overflow-y: auto;
		background-color: #fafafa;
	}

	tbody td {
		padding: 0 0 0 5px;
		white-space: pre-line;
		font-weight: 400;
	}

	.secondary-text {
		font-style: italic;
	}
}

.webQuote__items-table {
	overflow-y: auto;
}

.webQuote__actions,
.login-modal__actions {
	display: flex;
	justify-content: center;
	gap: 8px;
}

.webQuote__actions button:disabled {
	cursor: wait;
}

input:disabled {
	cursor: wait;
}

.login-modal {
	display: flex;
	flex-direction: column;
	gap: 8px;
	padding-top: 3px;
}

.login-form-fields {
	display: grid;
	grid-template-columns: max-content 1fr;
	column-gap: 8px;
	row-gap: 3px;
}

.login-modal__operationInfo {
	text-align: center;
}
</style>
